import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';

/** Documentación detallada (plantilla 12 apartados) — Restaurantes. */
export const restaurantesSections = [
  {
    id: "rest-pedidos",
    name: "Pedidos",
    type: "form",
    route: "/restaurantes/operaciones/pedidos",
    locationPath: "Restaurantes → Operaciones → Pedidos",
    generalDescription: "Toma y gestión de pedidos: ítems de carta/receta, cantidades, envío a cocina y cierre con pago según flujo del local.",
    prerequisites: [
      "Módulo Restaurantes habilitado.",
      "Usuario con acceso a restaurantes (admin o limitado con módulo)."
    ],
    interfaceElements: [
      {
        name: "Mesa o canal",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Origen del pedido.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Grilla de ítems",
        controlType: "Tabla",
        required: false,
        editable: true,
        automatic: false,
        description: "Platos y modificadores.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Enviar a cocina",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Despacha a espacio de producción.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Operaciones → Pedidos.",
      "Seleccione mesa.",
      "Agregue ítems del menú.",
      "Envíe a cocina y cierre cuenta al pagar."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Pedidos"
      },
      steps: [
        "Operaciones → Pedidos.",
        "Seleccione mesa.",
        "Agregue ítems del menú.",
        "Envíe a cocina y cierre cuenta al pagar."
      ]
    },
    validations: ['Revise campos obligatorios antes de enviar pedidos o guardar configuración.'],
    expectedResult: ['La operación de salón o cocina refleja los cambios (pedido, mesa o receta).'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "rest-mesas-operacion",
      "rest-produccion"
    ],
    observations: [
      "Módulo Restaurantes habilitado."
    ],
    screenshots: [
      SHOTS.vistaGeneral,
      SHOTS.listado
    ],
    roles: "Operación de salón y cocina."
  },

  {
    id: "rest-mesas-operacion",
    name: "Mesas y barras",
    type: "screen",
    route: "/restaurantes/operaciones/acceso",
    locationPath: "Restaurantes → Operaciones → Mesas y barras",
    generalDescription: "Mapa o listado operativo de mesas con estado (libre, ocupada, cuenta abierta). Punto de entrada rápida a pedidos.",
    prerequisites: [
      "Módulo Restaurantes habilitado.",
      "Usuario con acceso a restaurantes (admin o limitado con módulo)."
    ],
    interfaceElements: [
      {
        name: "Plano / lista de mesas",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Estados en color o ícono.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Abrir pedido",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Desde mesa libre u ocupada.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Revise mesas libres.",
      "Toque mesa para nuevo pedido o cuenta existente.",
      "Configure layout en Configuración → Mesas."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Mesas y barras"
      },
      steps: [
        "Revise mesas libres.",
        "Toque mesa para nuevo pedido o cuenta existente.",
        "Configure layout en Configuración → Mesas."
      ]
    },
    validations: ['Revise campos obligatorios antes de enviar pedidos o guardar configuración.'],
    expectedResult: ['La operación de salón o cocina refleja los cambios (pedido, mesa o receta).'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "rest-mesas-config",
      "rest-pedidos"
    ],
    observations: [
      "Módulo Restaurantes habilitado."
    ],
    screenshots: [
      SHOTS.vistaGeneral,
      SHOTS.listado
    ],
    roles: "Operación de salón y cocina."
  },

  {
    id: "rest-produccion",
    name: "Espacio de producción (cocina)",
    type: "screen",
    route: "/restaurantes/operaciones/produccion",
    locationPath: "Restaurantes → Operaciones → Espacio de producción",
    generalDescription: "Pantalla de cocina: ítems pendientes por estación, tiempos y marcado como listo para despacho a mesa.",
    prerequisites: [
      "Módulo Restaurantes habilitado.",
      "Usuario con acceso a restaurantes (admin o limitado con módulo)."
    ],
    interfaceElements: [
      {
        name: "Cola de ítems",
        controlType: "Tabla",
        required: false,
        editable: true,
        automatic: true,
        description: "Por cocina o prioridad.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Marcar listo",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Despacha al mozo o barra.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Monitoree pedidos entrantes.",
      "Prepare y marque ítems listos.",
      "Coordine con pedidos abiertos en salón."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Espacio de producción (cocina)"
      },
      steps: [
        "Monitoree pedidos entrantes.",
        "Prepare y marque ítems listos.",
        "Coordine con pedidos abiertos en salón."
      ]
    },
    validations: ['Revise campos obligatorios antes de enviar pedidos o guardar configuración.'],
    expectedResult: ['La operación de salón o cocina refleja los cambios (pedido, mesa o receta).'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "rest-pedidos",
      "rest-cocinas"
    ],
    observations: [
      "Módulo Restaurantes habilitado."
    ],
    screenshots: [
      SHOTS.vistaGeneral,
      SHOTS.listado
    ],
    roles: "Operación de salón y cocina."
  },

  {
    id: "rest-recetas",
    name: "Recetas",
    type: "form",
    route: "/restaurantes/clasificacion-recetas/recetas",
    locationPath: "Restaurantes → Configuración → Recetas",
    generalDescription: "Fichas de platos: ingredientes, rendimiento y costo para carta y pedidos. Comparte lógica con clasificación de recetas de producción.",
    prerequisites: [
      "Módulo Restaurantes habilitado.",
      "Usuario con acceso a restaurantes (admin o limitado con módulo)."
    ],
    interfaceElements: [
      {
        name: "Nombre del plato",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Visible en pedidos.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Ingredientes",
        controlType: "Grilla",
        required: false,
        editable: true,
        automatic: false,
        description: "Productos y cantidades.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Precio / IGV",
        controlType: "Número",
        required: false,
        editable: true,
        automatic: false,
        description: "Según política del local.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Clasifique antes en categorías si aplica.",
      "Cree ficha de receta.",
      "Asocie a cocina y lista de precios si integra ventas."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Recetas"
      },
      steps: [
        "Clasifique antes en categorías si aplica.",
        "Cree ficha de receta.",
        "Asocie a cocina y lista de precios si integra ventas."
      ]
    },
    validations: ['Revise campos obligatorios antes de enviar pedidos o guardar configuración.'],
    expectedResult: ['La operación de salón o cocina refleja los cambios (pedido, mesa o receta).'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "rest-clasif-categorias"
    ],
    observations: [
      "Módulo Restaurantes habilitado."
    ],
    screenshots: [
      SHOTS.vistaGeneral,
      SHOTS.listado
    ],
    roles: "Operación de salón y cocina."
  },

  {
    id: "rest-cocinas",
    name: "Cocinas",
    type: "form",
    route: "/restaurantes/configuraciones/cocinas",
    locationPath: "Restaurantes → Configuración → Cocinas",
    generalDescription: "Estaciones de preparación (cocina caliente, bar, postres). Los ítems de pedido se enrutan según cocina asignada en receta.",
    prerequisites: [
      "Módulo Restaurantes habilitado.",
      "Usuario con acceso a restaurantes (admin o limitado con módulo)."
    ],
    interfaceElements: [
      {
        name: "Nombre cocina",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Etiqueta en pantalla de producción.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Orden / prioridad",
        controlType: "Número",
        required: false,
        editable: true,
        automatic: false,
        description: "Visualización en cola.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Defina cocinas del local.",
      "Asigne cada receta a una cocina.",
      "Pruebe un pedido multiestación."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Cocinas"
      },
      steps: [
        "Defina cocinas del local.",
        "Asigne cada receta a una cocina.",
        "Pruebe un pedido multiestación."
      ]
    },
    validations: ['Revise campos obligatorios antes de enviar pedidos o guardar configuración.'],
    expectedResult: ['La operación de salón o cocina refleja los cambios (pedido, mesa o receta).'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "rest-produccion",
      "rest-recetas"
    ],
    observations: [
      "Módulo Restaurantes habilitado."
    ],
    screenshots: [
      SHOTS.vistaGeneral,
      SHOTS.listado
    ],
    roles: "Operación de salón y cocina."
  },

  {
    id: "rest-mesas-config",
    name: "Mesas (configuración)",
    type: "form",
    route: "/restaurantes/configuraciones/mesas",
    locationPath: "Restaurantes → Configuración → Mesas",
    generalDescription: "Alta de mesas y barras: código, capacidad y posición para el mapa operativo. No confundir con la vista Operaciones → Mesas y barras.",
    prerequisites: [
      "Módulo Restaurantes habilitado.",
      "Usuario con acceso a restaurantes (admin o limitado con módulo)."
    ],
    interfaceElements: [
      {
        name: "Número o nombre de mesa",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Identificador en salón.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Capacidad",
        controlType: "Número",
        required: false,
        editable: true,
        automatic: false,
        description: "Comensales.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Zona / salón",
        controlType: "Select",
        required: false,
        editable: true,
        automatic: false,
        description: "Agrupación visual.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Cree todas las mesas del local.",
      "Verifique en Operaciones → Mesas y barras.",
      "Ajuste si cambia el layout del salón."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Mesas (configuración)"
      },
      steps: [
        "Cree todas las mesas del local.",
        "Verifique en Operaciones → Mesas y barras.",
        "Ajuste si cambia el layout del salón."
      ]
    },
    validations: ['Revise campos obligatorios antes de enviar pedidos o guardar configuración.'],
    expectedResult: ['La operación de salón o cocina refleja los cambios (pedido, mesa o receta).'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "rest-mesas-operacion"
    ],
    observations: [
      "Módulo Restaurantes habilitado."
    ],
    screenshots: [
      SHOTS.vistaGeneral,
      SHOTS.listado
    ],
    roles: "Operación de salón y cocina."
  },

  {
    id: "rest-clasif-categorias",
    name: "Clasificación de recetas — Categorías",
    type: "form",
    route: "/restaurantes/clasificacion-recetas/categorias",
    locationPath: "Restaurantes → Configuración → Clasificación de recetas → Categorías",
    generalDescription: "Organiza la carta en categorías (entradas, fondos, bebidas) antes de subcategorías, familias y fichas.",
    prerequisites: [
      "Módulo Restaurantes habilitado.",
      "Usuario con acceso a restaurantes (admin o limitado con módulo)."
    ],
    interfaceElements: [
      {
        name: "Nombre categoría",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Menú y reportes.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Defina estructura de carta.",
      "Complete subcategorías y familias.",
      "Vincule recetas."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Clasificación de recetas — Categorías"
      },
      steps: [
        "Defina estructura de carta.",
        "Complete subcategorías y familias.",
        "Vincule recetas."
      ]
    },
    validations: ['Revise campos obligatorios antes de enviar pedidos o guardar configuración.'],
    expectedResult: ['La operación de salón o cocina refleja los cambios (pedido, mesa o receta).'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "rest-recetas"
    ],
    observations: [
      "Módulo Restaurantes habilitado."
    ],
    screenshots: [
      SHOTS.vistaGeneral,
      SHOTS.listado
    ],
    roles: "Operación de salón y cocina."
  },

  {
    id: "rest-clasif-jerarquia",
    name: "Clasificación de recetas — Vista jerárquica",
    type: "screen",
    route: "/restaurantes/clasificacion-recetas/jerarquia",
    locationPath: "Restaurantes → Configuración → Clasificación de recetas → Vista jerárquica",
    generalDescription: "Vista en árbol de toda la clasificación de platos del módulo restaurantes.",
    prerequisites: [
      "Módulo Restaurantes habilitado.",
      "Usuario con acceso a restaurantes (admin o limitado con módulo)."
    ],
    interfaceElements: [
      {
        name: "Árbol de recetas",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Categoría → subcategoría → familia → recetas.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Audite carta completa.",
      "Corrija niveles faltantes en pantallas específicas."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Clasificación de recetas — Vista jerárquica"
      },
      steps: [
        "Audite carta completa.",
        "Corrija niveles faltantes en pantallas específicas."
      ]
    },
    validations: ['Revise campos obligatorios antes de enviar pedidos o guardar configuración.'],
    expectedResult: ['La operación de salón o cocina refleja los cambios (pedido, mesa o receta).'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "rest-clasif-categorias",
      "rest-recetas"
    ],
    observations: [
      "Módulo Restaurantes habilitado."
    ],
    screenshots: [
      SHOTS.vistaGeneral,
      SHOTS.listado
    ],
    roles: "Operación de salón y cocina."
  }
].map((p) => functionDoc(p));
